// HUD arithmetic — PURE. No DOM, no imports, so tools/hudfeedback.mjs can
// prove it in plain Node. hud.js and toast.js hold the pixels; the mappings
// that are easy to get quietly wrong live here, where they can be tested.
//
// Three groups: the toast channel (severity, dedupe, hold, eviction), the
// noise meter curve, and the numeric health readout's colour/visibility.

// ---- toast channel ------------------------------------------------------
// 31 call sites share one lane. 25 of them live in files this workstream does
// not own (controller.js, main.js), and they all call `flashMove(string)`
// with no severity — so severity has to be INFERRED from the text for those,
// while the call sites that can pass it explicitly do.
//
// A keyword table is not elegant, but the alternative is either leaving
// "Sepsis — you need antibiotics NOW" in the same grey as "Reloading…" or
// editing six files owned by other agents mid-flight. The table is small,
// anchored on the exact strings in the tree (grep flashMove), and every call
// site that migrates to an explicit severity drops out of it.
export const SEV = { info: 0, warn: 1, crit: 2 };

// Matched in order; first hit wins. Keep these anchored to real strings.
const CRIT = [
  /\bsepsis\b/i,
  /you died/i,
  /^dead\b/i,
  /killed you/i,
  /\bbled out\b/i,
];
const WARN = [
  /\bbleeding\b/i,
  /bit you/i,
  /hit you/i,
  /claws at you/i,
  /very ill/i,
  /burning up/i,
  /shaking with fever/i,
  /looks dirty/i,
  /\bcannot\b/i,
  /\bpoison/i,
];

export function toastSeverity(msg, explicit) {
  if (explicit && Object.prototype.hasOwnProperty.call(SEV, explicit)) return explicit;
  const s = String(msg == null ? '' : msg);
  for (const re of CRIT) if (re.test(s)) return 'crit';
  for (const re of WARN) if (re.test(s)) return 'warn';
  return 'info';
}

// Dedupe key. "You are bleeding!" fired twice in two seconds is one piece of
// news, and three of them pushed the sepsis warning out of a three-row lane.
// Digits are REMOVED, not mapped to a placeholder: mapping them to '#' made
// "Dropped 3× Crackers" and "Dropped Crackers" two different rows, because one
// key had a '#' in it and the other had nothing there — so the same news split
// in two the moment a quantity appeared. Case, punctuation and the ellipsis
// that half the call sites use do not survive at all.
export function toastKey(msg, explicit) {
  if (explicit) return String(explicit);
  return String(msg == null ? '' : msg)
    .toLowerCase()
    .replace(/\d+/g, '')
    .replace(/[^a-z]+/g, ' ')
    .trim();
}

// How long a row holds before it starts to fade. A warning has to outlive the
// pickup chatter that is competing with it.
export function toastHold(sev) {
  return sev === 'crit' ? 5000 : sev === 'warn' ? 3600 : 2500;
}

// Which live row to drop when the lane is full. Lowest severity first, and
// the OLDEST of those — so a critical row is never evicted by pickup spam,
// which is the whole defect. `rows` is oldest-first. Returns an index, or -1
// when every live row outranks the incoming one (then the new row is dropped
// instead, which is the right answer: three crits on screen beat two crits
// and a "Reloading…").
export function evictIndex(rows, incomingSev = 'info') {
  let pick = -1;
  let worst = SEV[incomingSev] ?? 0;
  for (let i = 0; i < rows.length; i++) {
    const r = SEV[rows[i]] ?? 0;
    if (r < worst) { worst = r; pick = i; }
  }
  // all rows rank at or above the incoming one: evict the oldest row of equal
  // rank so the lane still moves, but never a row that outranks it
  if (pick < 0) {
    for (let i = 0; i < rows.length; i++) {
      if ((SEV[rows[i]] ?? 0) <= (SEV[incomingSev] ?? 0)) return i;
    }
  }
  return pick;
}

// ---- noise meter --------------------------------------------------------
// world.noiseState() already returns { n, v } every frame for free (main.js
// builds it from src/game/noise.js, whose header says the maths is pure and
// a meter may be added without touching the simulation — Z23). n is the
// radius in metres at which the infected can hear the player, 0–45.
//
// The player-visible range is NOT uniform across that span: the stance/speed
// table (noise.js BASE) tops out at 28 m for a standing sprint and the last
// 17 m only appear on wood or metal, so a linear bar would spend two thirds
// of its travel on cases the player rarely sees. The 0.7 exponent spreads the
// 4–18 m band — prone-walk to standing-run, which is where sneaking is
// actually decided — across three of the five segments.
export const NOISE_MAX = 45;      // noise.js MAX_RADIUS
export const NOISE_SEGS = 5;

export function noiseLevel(n) {
  const m = Math.max(0, Math.min(NOISE_MAX, Number(n) || 0));
  return +Math.pow(m / NOISE_MAX, 0.7).toFixed(4);
}

export function noiseMeter(state) {
  const n = state && Number.isFinite(state.n) ? Math.max(0, Math.min(NOISE_MAX, state.n)) : 0;
  const level = noiseLevel(n);
  // a radius of 0 is genuinely silent (standing still): the meter is not
  // "empty", it is ABSENT, the same rule the vitals column follows
  const bars = n <= 0 ? 0 : Math.max(1, Math.min(NOISE_SEGS, Math.ceil(level * NOISE_SEGS)));
  const tone = bars >= 4 ? 'loud' : bars === 3 ? 'heard' : 'quiet';
  return { n: Math.round(n), level, bars, tone, on: bars > 0 };
}

// ---- numeric health -----------------------------------------------------
// Mirrors inventory.js's `vitalColor` thresholds (55 / 32 / 16) so the number
// and the heart glyph beside it can never disagree about what colour "hurt"
// is. That constant is not exported from inventory.js and this workstream
// does not own the file; see handoff notes.
export function healthColor(v) {
  return v > 55 ? 'var(--hud-bone)' : v > 32 ? 'var(--hud-warn)'
    : v > 16 ? 'var(--hud-bad)' : 'var(--hud-crit)';
}

// Below 95 the heart glyph already appears in the vitals column (its own
// threshold), so the number arrives with it and the two read as one unit.
// At full health NOTHING is drawn — DayZ's status readouts are absent while
// nominal, and that is also what keeps the number out of the beauty shots.
export function healthVisible(v) {
  return Number.isFinite(v) && v < 95;
}

// Four colour buckets on a 24 px glyph is 0.24 px per hit point, so the
// number is the readout; round it, and never show a live player as 0.
export function healthText(v) {
  const n = Math.max(0, Math.min(100, Number(v) || 0));
  return String(n > 0 && n < 1 ? 1 : Math.round(n));
}
